import { useRef, useState } from "react";
import { api, type AlimentoBusca, type RespostaFoto, type SugestaoFoto } from "../lib/api.js";
import { Mostarda } from "../components/Mostarda.js";

type Item = {
  chave: string;
  nomeDetectado: string;
  foodId: string | null;
  nomeAlimento: string;
  gramas: number;
  confianca: number;
  fonte: string;
  verificado: boolean;
  alternativas: { foodId: string; name: string }[];
};

const SLOTS = [
  ["cafe", "Café"],
  ["lanche_manha", "Lanche"],
  ["almoco", "Almoço"],
  ["lanche_tarde", "Lanche"],
  ["jantar", "Jantar"],
  ["ceia", "Ceia"],
] as const;

/**
 * Captura por foto e a TELA DE REVISÃO.
 *
 * A revisão é a peça que separa este app do Cal AI. Modelos de visão erram ~40%
 * em energia e mais de 60% em proteína; a diferença não é o modelo, é admitir o
 * erro e deixar corrigir em dez segundos. Por isso:
 *
 * - o modelo devolve ALIMENTO e GRAMAS; os números vêm da TACO
 * - cada item mostra a confiança e dá para trocar por uma alternativa
 * - o slider de gramas usa passo grosso, porque o que importa é acertar a ordem
 *   de grandeza, não discutir 3 g
 * - nada é salvo até a pessoa confirmar
 */
export function Foto({ aoFechar }: { aoFechar: () => void }) {
  const entradaRef = useRef<HTMLInputElement>(null);
  const [fase, setFase] = useState<"capturar" | "analisando" | "revisar" | "salvando">("capturar");
  const [resposta, setResposta] = useState<RespostaFoto | null>(null);
  const [itens, setItens] = useState<Item[]>([]);
  const [slot, setSlot] = useState<string>(slotProvavel());
  const [erro, setErro] = useState<string | null>(null);

  async function analisar(arquivo: File) {
    setFase("analisando");
    setErro(null);
    try {
      const r = await api.postImage<RespostaFoto>("/meals/photo", arquivo);
      setResposta(r);
      setItens(r.suggestions.map(paraItem));
      if (r.error) setErro(r.error);
      setFase("revisar");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui enviar a foto.");
      setFase("capturar");
    }
  }

  async function salvar() {
    if (!resposta) return;
    setFase("salvando");
    try {
      await api.post("/meals", {
        slot,
        photoKey: resposta.photoKey,
        reviewed: true,
        items: itens.map((i) => ({
          ...(i.foodId ? { foodId: i.foodId } : { customName: i.nomeDetectado }),
          grams: i.gramas,
          confidence: i.confianca,
        })),
      });
      aoFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui salvar.");
      setFase("revisar");
    }
  }

  if (fase === "capturar" || fase === "analisando") {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 px-5">
        <Mostarda estado={fase === "analisando" ? "espreitando" : "cutucando"} tamanho={130} />
        <p className="text-center text-sm text-suave">
          {fase === "analisando"
            ? "Olhando teu prato…"
            : "Fotografa o prato inteiro, de cima, com um talher do lado pra eu ter referência de tamanho."}
        </p>

        {erro && <p className="text-sm text-alerta">{erro}</p>}

        <input
          ref={entradaRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const arquivo = e.target.files?.[0];
            if (arquivo) void analisar(arquivo);
          }}
        />

        <div className="flex w-full flex-col gap-2">
          <button
            className="botao-primario"
            disabled={fase === "analisando"}
            onClick={() => entradaRef.current?.click()}
          >
            {fase === "analisando" ? "Analisando…" : "Abrir câmera"}
          </button>
          <button className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
        </div>
      </main>
    );
  }

  const total = itens.reduce(
    (acc, i) => acc + estimaKcal(i),
    0,
  );

  return (
    <main className="mx-auto flex max-w-md flex-col gap-4 px-4 pb-32 pt-6">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Confere rapidinho</h1>
        <button className="text-sm text-suave underline" onClick={aoFechar}>
          Cancelar
        </button>
      </header>

      {erro && <p className="rounded-xl2 bg-alerta/10 px-4 py-3 text-sm text-alerta">{erro}</p>}

      {resposta?.trainsOnInput && (
        <p className="rounded-xl2 bg-mostarda/15 px-4 py-3 text-xs">
          Esta foto foi analisada pelo Gemini free tier, que usa o que recebe para
          treinar modelos do Google. Dá pra trocar em Ajustes.
        </p>
      )}

      {resposta && resposta.questions.length > 0 && (
        <section className="cartao">
          <span className="rotulo">O gato ficou na dúvida</span>
          <ul className="mt-1 list-disc pl-4 text-sm text-suave">
            {resposta.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {SLOTS.map(([valor, texto]) => (
          <button
            key={valor}
            onClick={() => setSlot(valor)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
              slot === valor ? "bg-mostarda font-semibold text-[rgb(32_28_22)]" : "border border-borda"
            }`}
          >
            {texto}
          </button>
        ))}
      </div>

      {itens.length === 0 && (
        <p className="text-sm text-suave">
          Não reconheci nada. Dá pra buscar na mão — a foto fica salva junto.
        </p>
      )}

      {itens.map((item, indice) => (
        <ItemRevisao
          key={item.chave}
          item={item}
          aoMudar={(novo) => setItens(itens.map((i, n) => (n === indice ? novo : i)))}
          aoRemover={() => setItens(itens.filter((_, n) => n !== indice))}
        />
      ))}

      <BuscaAlimento aoEscolher={(novo) => setItens([...itens, novo])} />

      <nav className="fixed inset-x-0 bottom-0 border-t border-borda bg-superficie/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center gap-3">
          <div className="min-w-0">
            <p className="rotulo">Total estimado</p>
            <p className="text-lg font-bold tabular-nums">{Math.round(total)} kcal</p>
          </div>
          <button
            className="botao-primario flex-1"
            disabled={itens.length === 0 || fase === "salvando"}
            onClick={salvar}
          >
            {fase === "salvando" ? "Salvando…" : "Registrar"}
          </button>
        </div>
      </nav>
    </main>
  );
}

function ItemRevisao({
  item,
  aoMudar,
  aoRemover,
}: {
  item: Item;
  aoMudar: (i: Item) => void;
  aoRemover: () => void;
}) {
  const baixa = item.confianca < 0.55;

  return (
    <section className="cartao flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold leading-tight">{item.nomeAlimento}</p>
          <p className="text-xs text-suave">
            {item.nomeDetectado !== item.nomeAlimento && `vi "${item.nomeDetectado}" · `}
            {item.fonte === "taco" ? "TACO" : item.fonte === "bootstrap" ? "valor aproximado" : item.fonte}
            {baixa && " · confiança baixa"}
          </p>
        </div>
        <button className="shrink-0 text-xs text-suave underline" onClick={aoRemover}>
          tirar
        </button>
      </div>

      <div className="flex items-center gap-3">
        {/* Passo de 5 g: o que importa é a ordem de grandeza. Discutir 3 g é a
            precisão falsa que faz a pessoa desistir de conferir. */}
        <input
          type="range"
          min={5}
          max={500}
          step={5}
          value={item.gramas}
          onChange={(e) => aoMudar({ ...item, gramas: Number(e.target.value), confianca: 1 })}
          className="h-8 flex-1 accent-[rgb(var(--c-mostarda))]"
          aria-label={`Gramas de ${item.nomeAlimento}`}
        />
        <span className="w-16 text-right font-semibold tabular-nums">{item.gramas} g</span>
      </div>

      {item.alternativas.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto">
          {item.alternativas.map((alt) => (
            <button
              key={alt.foodId}
              className="shrink-0 rounded-full border border-borda px-3 py-1 text-xs"
              onClick={() =>
                aoMudar({
                  ...item,
                  foodId: alt.foodId,
                  nomeAlimento: alt.name,
                  confianca: 1,
                  alternativas: item.alternativas
                    .filter((a) => a.foodId !== alt.foodId)
                    .concat({ foodId: item.foodId ?? "", name: item.nomeAlimento })
                    .filter((a) => a.foodId),
                })
              }
            >
              {alt.name}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function BuscaAlimento({ aoEscolher }: { aoEscolher: (i: Item) => void }) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState<AlimentoBusca[]>([]);

  async function buscar(valor: string) {
    setTermo(valor);
    if (valor.trim().length < 2) return setResultados([]);
    const { results } = await api.get<{ results: AlimentoBusca[] }>(
      `/foods/search?q=${encodeURIComponent(valor)}`,
    );
    setResultados(results);
  }

  return (
    <section className="cartao flex flex-col gap-2">
      <span className="rotulo">Faltou alguma coisa?</span>
      <input
        className="rounded-xl2 border border-borda bg-fundo px-3 py-2"
        value={termo}
        placeholder="buscar alimento"
        onChange={(e) => void buscar(e.target.value)}
      />
      {resultados.map((r) => (
        <button
          key={r.id}
          className="flex items-baseline justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-fundo"
          onClick={() => {
            aoEscolher({
              chave: `${r.id}-${Date.now()}`,
              nomeDetectado: r.name,
              foodId: r.id,
              nomeAlimento: r.name,
              gramas: r.defaultPortion?.grams ?? 100,
              confianca: 1,
              fonte: r.source,
              verificado: r.verified,
              alternativas: [],
            });
            setTermo("");
            setResultados([]);
          }}
        >
          <span className="min-w-0 truncate">{r.name}</span>
          <span className="shrink-0 text-xs text-suave">
            {r.defaultPortion?.label ?? "100 g"} · {Math.round(r.per100g.kcal)} kcal/100g
          </span>
        </button>
      ))}
    </section>
  );
}

function paraItem(s: SugestaoFoto): Item {
  return {
    chave: `${s.detectedName}-${Math.random().toString(36).slice(2, 8)}`,
    nomeDetectado: s.detectedName,
    foodId: s.match?.foodId ?? null,
    nomeAlimento: s.match?.name ?? s.detectedName,
    gramas: Math.max(5, Math.round(s.grams / 5) * 5),
    confianca: s.confidence,
    fonte: s.match?.source ?? "sem correspondência",
    verificado: s.match?.verified ?? false,
    alternativas: s.alternatives,
  };
}

/** Estimativa só para o rodapé — o valor autoritativo é calculado no servidor. */
function estimaKcal(item: Item): number {
  return item.foodId ? (item.gramas / 100) * 150 : 0;
}

function slotProvavel(): string {
  const h = new Date().getHours();
  if (h < 10) return "cafe";
  if (h < 12) return "lanche_manha";
  if (h < 15) return "almoco";
  if (h < 18) return "lanche_tarde";
  if (h < 22) return "jantar";
  return "ceia";
}
